import { AppError } from '../errors/AppError';
import {
  opPaginaCatalogoOrientacao,
  opDimensaoSvgGeradoCDI,
  opLinkForma,
  opLinkPapel,
  opLinkEstilo,
} from './const';

/**
 * Mantido como alias para não alterar as chamadas existentes deste arquivo.
 * A fonte única dos valores agora é opLinkForma em lib/const.js — antes
 * havia duas definições divergentes de LINK_FORM_TYPES no projeto.
 */
const LINK_FORM_TYPES = {
  circle: opLinkForma.CIRCULO,
  rect: opLinkForma.RETANGULO,
  ocr: opLinkForma.OCR,
  area: opLinkForma.AREA,
};

/* =====================================================================
   HELPERS DE GEOMETRIA DO MAPEAMENTO
   ---------------------------------------------------------------------
   O SVG das páginas é gerado com prefixo de namespace
   (ConverterImg2SVGService escreve <svg:svg>, <svg:g>, <svg:image>), e os
   elementos criados aqui usam createElementNS com nome qualificado
   ('svg:rect'). Por isso node.nodeName pode ser 'svg:rect' OU 'rect',
   dependendo de como a página foi gerada — sempre comparar pelo nome
   local, nunca pelo nodeName cru.
   ===================================================================== */

/**
 * Nome local do elemento, sem o prefixo de namespace e sempre em
 * minúsculas.
 *
 * A normalização de caixa é necessária: elementos criados/parseados no
 * namespace SVG mantêm o nome em minúsculas, mas quando o mesmo markup é
 * interpretado pelo parser de HTML (tag desconhecida, sem namespace) o
 * nodeName volta em MAIÚSCULAS. Sem isso a comparação falha silenciosamente
 * e o link simplesmente não se move.
 */
export const getNomeLocalSvg = (node) => {
  if (!node || !node.nodeName) return '';

  const nome = String(node.nodeName).toLowerCase();

  return nome.includes(':') ? nome.split(':').pop() : nome;
};

/** true quando o elemento é uma forma clicável de link */
export const isFormaLink = (node) =>
  ['circle', 'rect', 'ellipse'].includes(getNomeLocalSvg(node));

/** papel do elemento dentro do link (data-role) */
export const getPapelLink = (node) =>
  (node && node.dataset && node.dataset.role) || null;

/** forma principal (elemento clicável) de um link */
export const getFormaPrincipal = (link) =>
  (link && link.childNodes && link.childNodes[0]) || null;

/**
 * Caixa envolvente da forma principal, em unidades do SVG.
 * Não usa getBBox porque a forma pode estar oculta ou fora de tela.
 */
export const getCaixaForma = (forma) => {
  if (!forma) return null;

  const num = (attr) => Number(forma.getAttribute(attr)) || 0;

  if (getNomeLocalSvg(forma) === 'circle') {
    const r = num('r');
    return { x: num('cx') - r, y: num('cy') - r, width: r * 2, height: r * 2 };
  }

  if (getNomeLocalSvg(forma) === 'ellipse') {
    const rx = num('rx');
    const ry = num('ry');
    return {
      x: num('cx') - rx,
      y: num('cy') - ry,
      width: rx * 2,
      height: ry * 2,
    };
  }

  return {
    x: num('x'),
    y: num('y'),
    width: num('width'),
    height: num('height'),
  };
};

/**
 * Cria a linha de chamada entre a área e a etiqueta.
 *
 * Os extremos são guardados em data-x1/y1/x2/y2 porque o atributo "d"
 * pode ser reescrito em coordenadas relativas pelo svgo (ex.: o
 * "M335 90 L360 60" vira "M335 90l25-30"), o que tornaria o parse do "d"
 * frágil ao reabrir uma página já salva. Os data-* são preservados.
 */
export const criarLinhaChamada = (svgNamespace, x1, y1, x2, y2) => {
  const chamada = document.createElementNS(svgNamespace, 'svg:path');

  chamada.setAttribute('data-role', opLinkPapel.CHAMADA);
  chamada.setAttribute('stroke', opLinkEstilo.COR);
  chamada.setAttribute('stroke-width', opLinkEstilo.ESPESSURA);
  chamada.setAttribute('fill', 'none');

  atualizarLinhaChamada(chamada, x1, y1, x2, y2);

  return chamada;
};

/** atualiza os extremos e o "d" da linha de chamada */
export function atualizarLinhaChamada(chamada, x1, y1, x2, y2) {
  if (!chamada) return;

  const r = (v) => Math.round(Number(v) * 100) / 100;

  chamada.setAttribute('data-x1', r(x1));
  chamada.setAttribute('data-y1', r(y1));
  chamada.setAttribute('data-x2', r(x2));
  chamada.setAttribute('data-y2', r(y2));
  chamada.setAttribute('d', `M${r(x1)} ${r(y1)} L${r(x2)} ${r(y2)}`);
}

/** extremos da linha de chamada, lidos dos data-* (não do "d") */
export const getExtremosChamada = (chamada) => {
  if (!chamada) return null;

  return {
    x1: Number(chamada.getAttribute('data-x1')) || 0,
    y1: Number(chamada.getAttribute('data-y1')) || 0,
    x2: Number(chamada.getAttribute('data-x2')) || 0,
    y2: Number(chamada.getAttribute('data-y2')) || 0,
  };
};

/**
 * Translada um elemento do SVG em (dx, dy), tratando cada tipo de forma.
 * Usada para mover links compostos (área + chamada + etiqueta + texto),
 * em que a regra antiga de "childNodes[0] é a forma e childNodes[1] é o
 * texto" não se aplica.
 */
export const transladarElementoSvg = (el, dx, dy) => {
  if (!el || !el.getAttribute) return;

  const nome = getNomeLocalSvg(el);
  const num = (attr) => Number(el.getAttribute(attr)) || 0;

  switch (nome) {
    case 'circle':
    case 'ellipse':
      el.setAttribute('cx', num('cx') + dx);
      el.setAttribute('cy', num('cy') + dy);
      break;
    case 'rect':
    case 'image':
    case 'text':
      el.setAttribute('x', num('x') + dx);
      el.setAttribute('y', num('y') + dy);
      break;
    case 'path': {
      /** só sabemos transladar a linha de chamada, que guarda os extremos */
      const extremos = getExtremosChamada(el);
      if (!extremos) break;

      atualizarLinhaChamada(
        el,
        extremos.x1 + dx,
        extremos.y1 + dy,
        extremos.x2 + dx,
        extremos.y2 + dy
      );
      break;
    }
    default:
      break;
  }
};

/** translada todos os elementos internos de um link */
export const transladarLink = (link, dx, dy) => {
  if (!link || !link.childNodes) return;

  Array.from(link.childNodes).forEach((filho) =>
    transladarElementoSvg(filho, dx, dy)
  );
};

/**
 * Calcula a âncora da linha de chamada: sai do centro da borda da área
 * mais próxima da etiqueta e chega na borda da etiqueta mais próxima da
 * área. Evita que a linha atravesse as duas caixas.
 */
export const calcularAncoraChamada = (caixaArea, caixaRotulo) => {
  if (!caixaArea || !caixaRotulo) return null;

  const centroArea = {
    x: caixaArea.x + caixaArea.width / 2,
    y: caixaArea.y + caixaArea.height / 2,
  };
  const centroRotulo = {
    x: caixaRotulo.x + caixaRotulo.width / 2,
    y: caixaRotulo.y + caixaRotulo.height / 2,
  };

  const dx = centroRotulo.x - centroArea.x;
  const dy = centroRotulo.y - centroArea.y;

  /** eixo dominante define de qual lado a linha sai e chega */
  if (Math.abs(dx) >= Math.abs(dy)) {
    const x1 = dx >= 0 ? caixaArea.x + caixaArea.width : caixaArea.x;
    const x2 = dx >= 0 ? caixaRotulo.x : caixaRotulo.x + caixaRotulo.width;

    return { x1, y1: centroArea.y, x2, y2: centroRotulo.y };
  }

  const y1 = dy >= 0 ? caixaArea.y + caixaArea.height : caixaArea.y;
  const y2 = dy >= 0 ? caixaRotulo.y : caixaRotulo.y + caixaRotulo.height;

  return { x1: centroArea.x, y1, x2: centroRotulo.x, y2 };
};

/**
 * Largura estimada da etiqueta para um texto, em unidades do SVG.
 * Aproximação por número de caracteres — evita depender de medição no DOM
 * (getComputedTextLength falha em elemento ainda não renderizado).
 */
export const larguraEstimadaRotulo = (texto, fontSize) => {
  const conteudo = String(texto || '');
  const largura = Math.max(
    fontSize * 2,
    conteudo.length * fontSize * 0.62 + fontSize
  );

  /** arredonda para evitar ruído de ponto flutuante no atributo do SVG */
  return Math.round(largura * 100) / 100;
};

export const getSvgCoordinates = (selectorRef) => {
  const x = selectorRef.current.x.baseVal.value;
  const y = selectorRef.current.y.baseVal.value;
  const width = selectorRef.current.width.baseVal.value;
  const height = selectorRef.current.height.baseVal.value;

  return { x, y, width, height };
};

export const createOcrGroup = (rootRef) => {
  /** cria grupo para mapeamento do serviço de OCR */
  const ocrMapper = document.createElementNS(
    rootRef.current.namespaceURI,
    'svg:g',
    rootRef.current
  );
  ocrMapper.setAttribute('id', 'ocrGroup');
  rootRef.current.appendChild(ocrMapper);
  return ocrMapper;
};

export const createSvgLinkPdfProcess = (
  svgNamespace,
  ocrMapperRef,
  pt,
  index
) => {
  /** cria elemento */
  const link = document.createElementNS(svgNamespace, 'a');
  link.id = index;
  link.setAttribute('x', pt.center_x);
  link.setAttribute('y', pt.center_y - 5);
  link.setAttribute('class', '');
  // código preventivo para o link não ser executado no design
  link.addEventListener('click', (e) => e.preventDefault());
  ocrMapperRef.current.appendChild(link);

  /** cria retângulo ocr */
  const linkForm = document.createElementNS(svgNamespace, 'svg:rect');
  linkForm.setAttribute('x', pt.center_x + 5.5);
  linkForm.setAttribute('y', pt.center_y - 5);
  linkForm.setAttribute('height', '10px');
  linkForm.setAttribute('width', `${pt.text.length * 6}px`);
  linkForm.setAttribute('class', 'success');
  linkForm.setAttribute('data-id', LINK_FORM_TYPES.ocr);
  link.appendChild(linkForm);

  /** cria texto */
  const linkText = document.createElementNS(svgNamespace, 'svg:text');
  linkText.setAttribute('x', pt.center_x + 5.5);
  linkText.setAttribute('y', pt.center_y + 3);
  linkText.setAttribute('font-size', '10px');
  linkText.setAttribute('class', 'success');
  linkText.textContent = pt.text;
  link.appendChild(linkText);

  return link;
};

export const centerCalculation = (orientacaoImagem, x, y, width, height) => {
  let centroX;
  let centroY;

  switch (orientacaoImagem) {
    case opPaginaCatalogoOrientacao.LANDSCAPE:
      centroX = opDimensaoSvgGeradoCDI.LARGURA_PAISAGEM / 2 - (width / 2 + x);
      centroY = opDimensaoSvgGeradoCDI.ALTURA_PAISAGEM / 2 - (height / 2 + y);

      break;
    case opPaginaCatalogoOrientacao.PORTRAIT:
      centroX = opDimensaoSvgGeradoCDI.LARGURA_RETRATO / 2 - (width / 2 + x);
      centroY = opDimensaoSvgGeradoCDI.ALTURA_RETRATO / 2 - (height / 2 + y);
      break;
    default:
      throw new AppError('Orientação de imagem inválida');
  }

  return { centroX, centroY };
};

export const updateSvgPositionProcessPdf = (
  svgRef,
  rootRef,
  coordX,
  coordY
) => {
  let transformMatrix = svgRef.current.createSVGMatrix();
  const { baseVal } = rootRef.current.transform;

  const baseIndex = Array.from(baseVal).findIndex((e) => e.type === 1);
  if (baseIndex >= 0) {
    const { matrix } = baseVal.getItem(baseIndex);
    transformMatrix = matrix;
  }

  transformMatrix.e += coordX;
  transformMatrix.f += coordY;

  const transform = baseVal.createSVGTransformFromMatrix(transformMatrix);
  baseVal.clear();
  baseVal.appendItem(transform);
};

export const centerSvgPdfProcess = (
  svgRef,
  rootRef,
  centralizacaoX,
  centralizacaoY
) => {
  let transformMatrix = svgRef.current.createSVGMatrix();
  const { baseVal } = rootRef.current.transform;

  const baseIndex = Array.from(baseVal).findIndex((e) => e.type === 1);
  if (baseIndex >= 0) {
    const { matrix } = baseVal.getItem(baseIndex);
    transformMatrix = matrix;
  }

  transformMatrix.e = centralizacaoX;
  transformMatrix.f = centralizacaoY;

  const transform = baseVal.createSVGTransformFromMatrix(transformMatrix);

  baseVal.clear();
  baseVal.appendItem(transform);
};

export const scaleSvgProcessPdf = (svgRef, rootRef, rootScaleRef, valor) => {
  let transformMatrix = svgRef.current.createSVGMatrix();
  const { baseVal } = rootRef.current.transform;

  const baseIndex = Array.from(baseVal).findIndex((e) => e.type === 1);
  if (baseIndex >= 0) {
    const { matrix } = baseVal.getItem(baseIndex);
    transformMatrix = matrix;
  }

  transformMatrix.a = rootScaleRef.current + valor;
  transformMatrix.d = rootScaleRef.current + valor;

  rootScaleRef.current += valor;

  const transform = baseVal.createSVGTransformFromMatrix(transformMatrix);
  baseVal.clear();
  baseVal.appendItem(transform);
};
